import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "caption"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "caption"
}
const _hoisted_4 = { class: "invite" }
const _hoisted_5 = {
  key: 0,
  class: "alert"
}
const _hoisted_6 = {
  key: 1,
  class: "italic"
}
const _hoisted_7 = {
  key: 1,
  class: "caption"
}
const _hoisted_8 = ["href"]
const _hoisted_9 = {
  key: 2,
  class: "caption"
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 3,
  class: "caption"
}
const _hoisted_12 = {
  key: 2,
  class: "caption"
}

import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import { InviteRequest } from "@/models/api";
import { Applicant } from "@/models/application";
import AlertModal from "@/components/modals/AlertModal.vue";
import { ApplicantTypeIds } from "@/config/constants";


export default /*@__PURE__*/_defineComponent({
  __name: 'NotSubmittedText',
  setup(__props) {

const store = useStore();

const applicationId = computed(() => store?.state?.appData?.application?.id);
const currentApplicant = computed(() => store?.state?.appData?.currentApplicant);

const restartLink = computed<string>(
  () =>
    store?.state?.configuration?.borrowerApplicationUrl,
);

// The configured link carries the lender/program query; the return endpoints hang off its host and path.
const borrowerAppBaseUrl = computed<string>(() => {
  const url = new URL(store.state.configuration.borrowerApplicationUrl);
  return `${url.origin}${url.pathname}`.replace(/\/$/, "");
});

const primaryApplicantTypeDisplayName = computed<string>(
  () => store.getters["primaryApplicantTypeDisplayName"],
);

const coApplicantTypeDisplayName = computed<string>(
  () => store.getters["coApplicantTypeDisplayName"],
);

const invitedApplicantTypeDisplay = computed(() => {
  return secondaryApplicantInfo.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT ||
    invitedApplicantInfo.value?.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT
    ? primaryApplicantTypeDisplayName.value?.toLowerCase()
    : coApplicantTypeDisplayName.value?.toLowerCase();
});

const invitedApplicantInfo = computed<Applicant>(() => {
  if ((store?.state?.appData?.applicants || []).length > 1) {
    return store?.state?.appData?.applicants.find(
      (a: Applicant) => a.applicantTypeId !== currentApplicant.value?.applicantTypeId,
    );
  } else {
    return {} as Applicant;
  }
});

const soloApp = computed<boolean>(
  () => store?.state?.appData?.application?.metadata?.withCoApplicants === false,
);

const gettingData = ref<boolean>(true);
const isInvitedApplicant = ref<boolean | undefined>(undefined);
const isSecondaryApplicant = ref<boolean | undefined>(undefined);
const invite = ref({} as any);
const inviteAccepted = ref<boolean | undefined>(undefined);
const inviteResent = ref<boolean | undefined>(undefined);
const resendInviteError = ref<boolean>(false);
const secondaryApplicantIsRegistered = ref<boolean | undefined>(undefined);
const secondaryApplicantInfo = ref({} as Applicant);
const showRestartLink = ref<boolean | undefined>(undefined);
const showResendInviteLink = ref<boolean | undefined>(undefined);
const showReturnLink = ref<boolean | undefined>(undefined);

async function redirectToBorrowerApp() {
  let link = await $api.return.getReturnLink(
    borrowerAppBaseUrl.value,
    store.state.appData.application.id,
  );

  location.href = `${borrowerAppBaseUrl.value}/return?applicationId=${store.state.appData.application.id}&token=${link?.data?.token}`;
}

async function resendInvite() {
  const appId = applicationId.value;
  if (inviteResent.value === undefined || inviteResent.value === false) {

    let inviteRequest = {
      applicationId: appId,
      invitedByApplicantId: currentApplicant.value?.id,
      invitedApplicantTypeId: store.state.invites[0]?.applicantTypeId,
      firstName: store.state.invites[0]?.firstName,
      middleName: store.state.invites[0]?.middleName,
      lastName: store.state.invites[0]?.lastName,
      emailAddress: store.state.invites[0]?.emailAddress,
    } as InviteRequest;
    // Get current invite and use the response to build the request body
    let response = await $api.applications.resendInvite(appId, inviteRequest);
    if (response?.err || response?.error || response?.invites[0].resendAtUtc === null) {
      resendInviteError.value = true;
    } else {
      inviteResent.value = true;
    }
  }
}

async function getApplicationStatusInfo() {
  gettingData.value = true;
  // Solo app
  if (soloApp.value) {
    showReturnLink.value =
      currentApplicant.value?.submittedAt === null ||
      currentApplicant.value?.submittedAt === undefined;
  }
  // Joint app
  if (!soloApp.value) {
    let res = (await $api.applications.getApplicationInvites(applicationId.value)) || [];

    if (res?.error || res?.length <= 0) {
      /************** NORMAL FLOW CHECK isPresent = true **************/
      showResendInviteLink.value = false;

      // primary applicant is logged in and left off on isPresent step show return link
      if (store?.state?.appData?.applicants?.length <= 1) {
        isSecondaryApplicant.value = false;
        showReturnLink.value = true;
      }
      // Secondary applicant exists
      if (store?.state?.appData?.applicants?.length > 1) {
        secondaryApplicantInfo.value = store?.state?.appData?.applicants?.pop();
        isSecondaryApplicant.value =
          currentApplicant.value?.applicantTypeId === secondaryApplicantInfo.value?.applicantTypeId;

        // Secondary applicant is logged in and has not submitted their portion
        if (isSecondaryApplicant.value === true && currentApplicant.value?.submittedAt === null) {
          showReturnLink.value = true;
        }

        // Primary applicant is logged in
        if (isSecondaryApplicant.value === false) {
          // scenario: Primary applicant has NOT submitted their portion yet.
          if (currentApplicant.value?.submittedAt === null) {
            showReturnLink.value = true;
            showRestartLink.value = false;
          }
          // scenario: secondary applicant is registered
          if (secondaryApplicantInfo.value?.userId !== null) {
            showRestartLink.value = false;
            showReturnLink.value = false;
            secondaryApplicantIsRegistered.value = true;
          }
          // scenario: secondary applicant abandoned application before registering
          if (
            currentApplicant.value?.submittedAt !== null &&
            secondaryApplicantInfo.value?.userId === null
          ) {
            showRestartLink.value = true;
            showReturnLink.value = false;
            secondaryApplicantIsRegistered.value = false;
          }
        }
      }
    } else {
      /************** INVITE FLOW CHECK isPresent = false **************/
      let invites = res.invites;
      if ((invites || []).length > 0) {
        let invitedApplicant = invites[0];
        isInvitedApplicant.value =
          invitedApplicant.applicantTypeId === currentApplicant.value?.applicantTypeId;
        inviteAccepted.value = invitedApplicant?.accepted;
        invite.value = invites[0];
        store.dispatch("setInvites", invites);

        // Check if invite has been resent
        if (invitedApplicant.resendAtUtc !== null) {
          inviteResent.value = true;
        } else {
          inviteResent.value = false;
        }
        // Secondary applicant is logged in and has not submitted their portion
        if (
          isInvitedApplicant.value === true &&
          (store?.state?.appData?.currentApplicant?.submittedAt === null ||
            store?.state?.appData?.currentApplicant?.submittedAt === undefined)
        ) {
          showReturnLink.value = true;
        }

        // Primary applicant is logged in
        if (isInvitedApplicant.value === false) {
          showResendInviteLink.value = true;
          showReturnLink.value = undefined;

          // 1. Invite not accepted yet
          if (invitedApplicant?.accepted === false) {
            showResendInviteLink.value = true;
          }

          // 2. Invite accepted, but no username created
          if (
            invitedApplicant?.accepted === true &&
            (invitedApplicantInfo.value?.userId === null ||
              invitedApplicantInfo.value?.userId === undefined)
          ) {
            showResendInviteLink.value = false;
            showRestartLink.value = true;
          }

          // 3. Invite accepted, secondary applicant IS registered
          if (invitedApplicant?.accepted === true && invitedApplicantInfo.value?.userId !== null) {
            showResendInviteLink.value = false;
            showRestartLink.value = false;
            secondaryApplicantIsRegistered.value = true;
          }
        }
      }
    }
  }

  gettingData.value = false;
}

onMounted(async () => {
  await getApplicationStatusInfo();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (soloApp.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[3] || (_cache[3] = _createElementVNode("p", null, "Your application must be submitted before the expiration date above to be considered.", -1)),
          (showReturnLink.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
                _cache[1] || (_cache[1] = _createTextVNode(" Click ", -1)),
                _createElementVNode("a", {
                  class: "inline-link",
                  onClick: redirectToBorrowerApp
                }, "here"),
                _cache[2] || (_cache[2] = _createTextVNode(" to review your application. ", -1))
              ]))
            : _createCommentVNode("", true)
        ]))
      : (!soloApp.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (showResendInviteLink.value === true)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _cache[5] || (_cache[5] = _createElementVNode("p", null, " We are currently waiting for your invited applicant to complete their portion of the application. They must submit their portion before the expiration date above or the application will be cancelled. ", -1)),
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("a", { onClick: resendInvite }, [
                      _cache[4] || (_cache[4] = _createTextVNode("Do you want to resend the invite?", -1)),
                      _createElementVNode("i", {
                        class: _normalizeClass(["icon-ok-circle", [inviteResent.value ? 'complete' : 'incomplete']])
                      }, null, 2)
                    ]),
                    (inviteResent.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_5, "⠀Invite resent!"))
                      : (_openBlock(), _createElementBlock("span", _hoisted_6, "⠀<<⠀Click to resend."))
                  ])
                ]))
              : (showRestartLink.value === true)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[8] || (_cache[8] = _createElementVNode("p", null, " Unfortunately, the second applicant on your application did not register, so you will need to restart the application process. ", -1)),
                    _createElementVNode("p", null, [
                      _cache[6] || (_cache[6] = _createTextVNode(" Follow this ", -1)),
                      _createElementVNode("a", {
                        class: "inline-link",
                        href: restartLink.value
                      }, "link", 8, _hoisted_8),
                      _cache[7] || (_cache[7] = _createTextVNode(" to begin again. ", -1))
                    ])
                  ]))
                : (showReturnLink.value === true)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _cache[11] || (_cache[11] = _createElementVNode("p", null, "Your application must be submitted before the expiration date above to be considered.", -1)),
                      (showReturnLink.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_10, [
                            _cache[9] || (_cache[9] = _createTextVNode(" Click ", -1)),
                            _createElementVNode("a", {
                              class: "inline-link",
                              onClick: redirectToBorrowerApp
                            }, "here"),
                            _cache[10] || (_cache[10] = _createTextVNode(" to review your application. ", -1))
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : (secondaryApplicantIsRegistered.value === true)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createElementVNode("p", null, " We're just waiting on your " + _toDisplayString(invitedApplicantTypeDisplay.value) + " to finish their part of the application. Have them log into their applicant portal and click the link to continue. ", 1)
                      ]))
                    : _createCommentVNode("", true)
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[12] || (_cache[12] = [
            _createElementVNode("p", null, "Your application must be submitted before the expiration date above to be considered.", -1)
          ]))])),
    (resendInviteError.value === true)
      ? (_openBlock(), _createBlock(AlertModal, {
          key: 3,
          onClose: _cache[0] || (_cache[0] = ($event: any) => {
      {
        resendInviteError.value = false;
      }
    }),
          alertText: 'We\'re sorry. We cannot complete your request at this time. Please try again later.'
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})